import React from 'react';
import type { FactorType } from '@/features/onboarding/types/onboarding.types';
import { CompleteStepError } from './complete-step-error';
import { CompleteStepExchanging } from './complete-step-exchanging';
import { CompleteStepSuccess } from './complete-step-success';
import { useCompleteStep } from './use-complete-step';

interface CompleteStepProps {
  enrolledFactors: FactorType[];
  userName?: string;
  isExchanging: boolean;
  isAuthenticated: boolean;
  exchangeError: string | null;
  onExchange: () => Promise<boolean>;
  onComplete: () => void | Promise<void>;
}

/**
 * Complete Step - Final step of onboarding wizard
 *
 * Flow:
 * 1. Auto-triggers JWT exchange on mount
 * 2. Shows "Finishing setup..." while exchanging
 * 3. Shows error state if exchange fails (with retry)
 * 4. Shows success state when authenticated
 * 5. User clicks button to redirect
 */
export const CompleteStep: React.FC<CompleteStepProps> = ({
  enrolledFactors,
  userName,
  isExchanging,
  isAuthenticated,
  exchangeError,
  onExchange,
  onComplete,
}) => {
  // Handle auto-exchange on mount
  useCompleteStep({
    isAuthenticated,
    isExchanging,
    exchangeError,
    onExchange,
  });

  // Render appropriate state
  if (isExchanging) {
    return <CompleteStepExchanging />;
  }

  if (exchangeError) {
    return <CompleteStepError error={exchangeError} onRetry={onExchange} />;
  }

  // Only show success when authentication is complete
  if (!isAuthenticated) {
    return <CompleteStepExchanging />;
  }

  return (
    <CompleteStepSuccess
      userName={userName}
      enrolledFactors={enrolledFactors}
      onComplete={onComplete}
    />
  );
};
